// This component was taken from
// https://gitlab.internal.equipmentshare.com/pim/pim-ui/-/blob/main/src/components/products/filters/shared.tsx
import millify from "millify";
import React, { useEffect, useState } from "react";

import {
  BaseFilter,
  BaseFilterProps,
} from "../components/common/filters-v2/base";
import {
  FilterCheckboxList,
  FilterCheckboxListItem,
  FilterCheckboxListItems,
  FilterCheckboxListProps,
} from "../components/common/filters-v2/checkbox-list";

type FilterFn<T extends FilterCheckboxListItem> = ({
  searchValue,
  item,
}: {
  searchValue: string;
  item: T;
}) => boolean;

type FilterWithCheckboxListProps<
  T extends FilterCheckboxListItem = FilterCheckboxListItem,
> = {
  items: Array<T>;
  value?: string[];
  onChange: (value: string[]) => void;
  adornment?: FilterCheckboxListProps<T>["adornment"];
  filterFn?: FilterFn<T>;
} & BaseFilterProps;

/**
 * A wrapper around the base filter component that provides
 * a checkbox list with "draft" item selection state management
 */
export function FilterWithCheckboxList<T extends FilterCheckboxListItem>({
  items,
  value = [],
  onChange,
  adornment,
  filterFn,
  ...rest
}: FilterWithCheckboxListProps<T>) {
  const [selectedItems, setSelectedItems] = useState<string[]>(value);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  const handleSelectItem = (itemId: string) => {
    let nextSelectedItems = [...selectedItems];
    if (nextSelectedItems.includes(itemId)) {
      nextSelectedItems = nextSelectedItems.filter((id) => id !== itemId);
    } else {
      nextSelectedItems.push(itemId);
    }
    setSelectedItems(nextSelectedItems);
    setHasChanges(true);
  };

  const handleApply = () => {
    setHasChanges(false);
    onChange(selectedItems);
  };

  const handleClose = () => {
    setHasChanges(false);
    setSelectedItems(value);
  };

  const handleClear = () => {
    onChange([]);
    setSelectedItems([]);
  };

  let searchPlaceholder = "Search";
  if (items) {
    const millified = millify(items.length, { lowercase: true });
    searchPlaceholder = `Search ${millified} ${rest.label.toLowerCase()}s`;
  }

  return (
    <BaseFilter
      onClose={handleClose}
      hasChanges={hasChanges}
      appliedCount={value.length}
      onApplyChanges={handleApply}
      filterable={items.length > 5}
      onClear={handleClear}
      {...rest}
      slotProps={{
        ...rest.slotProps,
        searchInput: {
          placeholder: searchPlaceholder,
          ...rest.slotProps?.searchInput,
        },
      }}
      slots={{
        content: ({ searchValue }) => {
          let filteredItems = items;
          if (searchValue) {
            filteredItems = items.filter((i) => {
              if (filterFn) {
                return filterFn({ searchValue, item: i });
              }

              return i.name.toLowerCase().includes(searchValue.toLowerCase());
            });
          }

          return (
            <FilterCheckboxList
              items={filteredItems}
              highlightValue={searchValue}
              selectedItemIds={selectedItems}
              onSelectItem={(itemId: string) => handleSelectItem(itemId)}
              adornment={adornment}
            />
          );
        },
      }}
    />
  );
}

type FacetFilterProps = Omit<FilterWithCheckboxListProps, "items"> & {
  items?: Record<string, { count: number }>;
};

/**
 * A wrapper around the FilterWithCheckboxList above which
 * deals with "generic" facet filters e.g. Year/Variant
 */
export const FacetFilter = ({
  items,
  value = [],
  ...rest
}: FacetFilterProps) => {
  let itemsAsArray: FilterCheckboxListItems = [];

  if (items && Object.entries(items).length) {
    itemsAsArray = Object.entries(items).reduce<FilterCheckboxListItems>(
      (acc, [name, { count }]) => {
        return [...acc, { id: name, name, count }];
      },
      [],
    );
  }

  // Ensure any selected facet is present in
  // the list of options provided to the checkbox list
  if (value.length) {
    value.forEach((v) => {
      const isAlreadyPresent = itemsAsArray.find((i) => i.id === v);
      if (isAlreadyPresent) return;

      itemsAsArray.push({
        id: v,
        name: v,
        count: 0,
      });
    });
  }

  // If there are no options available for this facet, return nothing
  if (!itemsAsArray.length) return null;

  return (
    <FilterWithCheckboxList {...rest} items={itemsAsArray} value={value} />
  );
};
